import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';

// ─────────────────────────────────────────────────────────────────────────────
// 1) CONSTANTS & HELPERS
// ─────────────────────────────────────────────────────────────────────────────

// Same parser from DivorceTimeline
function parseMonths(str) {
  if (!str) return 0;
  const rangeMatch = str.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    const [_, minStr, maxStr] = rangeMatch;
    const minVal = parseFloat(minStr);
    const maxVal = parseFloat(maxStr);
    return (minVal + maxVal) / 2;
  }
  const singleMatch = str.match(/(\d+(?:\.\d+)?)/);
  return singleMatch ? parseFloat(singleMatch[1]) : 0;
}

// Default durations (example)
const DEFAULT_DOCS_MIN = 0.5;
const DEFAULT_DOCS_MAX = 1;
const DEFAULT_REDACTION = 1.0;

// Additional "complex" signification logic constants
const hagueCountries = [
  { code: "AL", name: "Albanie" },
  { code: "DE", name: "Allemagne" },
  { code: "AD", name: "Andorre" },
  { code: "AG", name: "Antigua-et-Barbuda" },
  { code: "AR", name: "Argentine" },
  { code: "AM", name: "Arménie" },
  { code: "AU", name: "Australie" },
  { code: "AT", name: "Autriche" },
  { code: "AZ", name: "Azerbaïdjan" },
  { code: "BS", name: "Bahamas" },
  { code: "BB", name: "Barbade" },
  { code: "BY", name: "Bélarus" },
  { code: "BE", name: "Belgique" },
  { code: "BZ", name: "Belize" },
  { code: "BA", name: "Bosnie-Herzégovine" },
  { code: "BW", name: "Botswana" },
  { code: "BR", name: "Brésil" },
  { code: "BG", name: "Bulgarie" },
  { code: "CA", name: "Canada" },
  { code: "CL", name: "Chili" },
  { code: "CN", name: "Chine" }, // Note: Applies to Hong Kong SAR and Macao SAR as well
  { code: "CY", name: "Chypre" },
  { code: "CO", name: "Colombie" },
  { code: "CR", name: "Costa Rica" },
  { code: "HR", name: "Croatie" },
  { code: "DK", name: "Danemark" },
  { code: "DO", name: "République dominicaine" },
  { code: "EG", name: "Égypte" },
  { code: "SV", name: "El Salvador" },
  { code: "EE", name: "Estonie" },
  { code: "US", name: "États-Unis d'Amérique" },
  { code: "FI", name: "Finlande" },
  { code: "FR", name: "France" },
  { code: "GE", name: "Géorgie" },
  { code: "GR", name: "Grèce" },
  { code: "HU", name: "Hongrie" },
  { code: "IN", name: "Inde" },
  { code: "IE", name: "Irlande" },
  { code: "IS", name: "Islande" },
  { code: "IL", name: "Israël" },
  { code: "IT", name: "Italie" },
  { code: "JP", name: "Japon" },
  { code: "KZ", name: "Kazakhstan" },
  { code: "KW", name: "Koweït" },
  { code: "LV", name: "Lettonie" },
  { code: "LT", name: "Lituanie" },
  { code: "LU", name: "Luxembourg" },
  { code: "MW", name: "Malawi" },
  { code: "MT", name: "Malte" },
  { code: "MH", name: "Îles Marshall" },
  { code: "MX", name: "Mexique" },
  { code: "MC", name: "Monaco" },
  { code: "ME", name: "Monténégro" },
  { code: "MA", name: "Maroc" },
  { code: "NL", name: "Pays-Bas" },
  { code: "NI", name: "Nicaragua" },
  { code: "MK", name: "Macédoine du Nord" },
  { code: "NO", name: "Norvège" },
  { code: "PK", name: "Pakistan" },
  { code: "PY", name: "Paraguay" },
  { code: "PH", name: "Philippines" },
  { code: "PL", name: "Pologne" },
  { code: "PT", name: "Portugal" },
  { code: "KR", name: "République de Corée" },
  { code: "MD", name: "République de Moldova" },
  { code: "RO", name: "Roumanie" },
  { code: "GB", name: "Royaume-Uni" },
  { code: "RU", name: "Fédération de Russie" },
  { code: "VC", name: "Saint-Vincent-et-les Grenadines" },
  { code: "SM", name: "Saint-Marin" },
  { code: "RS", name: "Serbie" },
  { code: "SC", name: "Seychelles" },
  { code: "SG", name: "Singapour" },
  { code: "SK", name: "Slovaquie" },
  { code: "SI", name: "Slovénie" },
  { code: "ES", name: "Espagne" },
  { code: "LK", name: "Sri Lanka" },
  { code: "SE", name: "Suède" },
  { code: "CH", name: "Suisse" },
  { code: "TN", name: "Tunisie" },
  { code: "TR", name: "Türkiye" },
  { code: "UA", name: "Ukraine" },
  { code: "VE", name: "Venezuela" },
  { code: "VN", name: "Viet Nam" },
].map((c) => ({ ...c, isSignatory: true }));

const nonHagueCountries = [
  { code: "DZ", name: "Algérie" },
  // { code: "CN", name: "Chine" }, // Now in hagueCountries
  // { code: "IN", name: "Inde" }, // Now in hagueCountries
].map((c) => ({ ...c, isSignatory: false }));

// Combined
const allCountries = [...hagueCountries, ...nonHagueCountries].sort((a, b) =>
  a.name.localeCompare(b.name)
);

/**
 * Dynamically calculates signification delays exactly like in DivorceTimeline
 */
function calculateSignificationDelay(answers) {
  const stepSignif = answers["step_signification"] || {};
  const stepProv = answers["pension_strategies"] || {}; // If you have a "strategies" step

  // If urgent measures (hypothetical)
  if (stepProv.mesures_provisoires === "oui") {
    return {
      min: 0.1, // ~3 days
      max: 0.3, // ~10 days
      msg: "[Signification] Urgente (mesures provisoires): 3-10 jours",
    };
  }

  // Address known
  if (stepSignif.adresse_exacte === "oui" || stepSignif.contacts_recents === "oui") {
    switch (stepSignif.localisation) {
      case "quebec":
        return {
          min: 0.25,
          max: 0.5,
          msg: "[Signification] Québec - standard: 1-2 semaines",
        };
      case "canada":
        return {
          min: 0.25,
          max: 0.75,
          msg: "[Signification] Canada hors QC: 1-3 semaines",
        };
      case "etranger": {
        const c = allCountries.find((x) => x.code === stepSignif.pays_etranger);
        if (c?.isSignatory) {
          return {
            min: 0.5,
            max: 2,
            msg: `[Signification] ${c.name} (Convention La Haye): 0.5-2 mois`,
          };
        }
        // Non-Hague
        return {
          min: 1,
          max: 6,
          msg: `[Signification] ${
            c ? c.name : "Pays inconnu"
          } (Hors Convention): 1-6 mois`,
        };
      }
      default:
        // fallback
        return { min: 0.5, max: 1, msg: "[Signification] Québec (défaut)" };
    }
  }

  // If "contacts_electroniques" or "residence_canada" => mode spécial
  if (
    stepSignif.contacts_electroniques === "oui" ||
    stepSignif.residence_canada === "oui"
  ) {
    if (stepSignif.mode_special_decision === "accepte") {
      return {
        min: 0.25,
        max: 0.5,
        msg: "[Signification] Mode spécial autorisé: 1-2 semaines",
      };
    }
    if (stepSignif.mode_special_decision === "refuse") {
      return {
        min: 0.5,
        max: 1,
        msg: "[Signification] Mode spécial refusé => 2-4 semaines de délai supplémentaire",
      };
    }
    // awaiting judge
    return {
      min: 0.5,
      max: 1,
      msg: "[Signification] En attente d'autorisation du tribunal",
    };
  }

  // Full unknown scenario
  return {
    min: 2,
    max: 4,
    msg: "[Signification] Complexe - consultation avocat recommandée",
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 2) STEPS DEFINITIONS
// ─────────────────────────────────────────────────────────────────────────────

// PHASE 1: PREPARATION
const preparationSteps = [
  {
    id: "pension_prep",
    phase: "preparation",
    title: "Préparation du dossier (jugement initial, preuves de revenus, etc.)",
    minDuration: DEFAULT_DOCS_MIN,
    maxDuration: DEFAULT_DOCS_MAX,
    shortContent: "Rassemblez tous vos justificatifs et discutez avec l'autre partie.",
    detailedContent: `
      Avant de commencer la procédure pour modifier ou annuler la pension, 
      assurez-vous de rassembler :
      <ul class="list-disc list-inside">
        <li>Le jugement actuel ou l'entente homologuée ordonnant la pension</li>
        <li>Vos documents financiers récents (feuilles de paie, avis d'imposition)</li>
        <li>Preuves de changement significatif (si vous demandez une révision)</li>
      </ul>
      Vérifiez si l'autre partie est d'accord pour procéder par voie simplifiée (entente).
    `,
    questions: [
      {
        id: "docs_delay",
        label: "Durée (en mois) pour rassembler vos documents ? (ex : '1' ou '2-3')",
        type: "text",
        help: "Laissez vide pour utiliser la durée par défaut (0.5 à 1 mois).",
      },
      {
        id: "q_entente",
        label: "L'autre partie est-elle d'accord pour la modification de pension ?",
        options: [
          { value: "oui", label: "Oui (entente possible)" },
          { value: "non", label: "Non (contestation)" },
        ],
      },
    ],
  },
];

// PHASE 2: PROCESSUS JUDICIAIRE (SAME FUNCTIONALITIES AS DIVORCE TIMELINE)
const judiciaireSteps = [
  // Step = Rédaction et dépôt de la demande
  {
    id: "pension_step3",
    phase: "judiciaire",
    title: "Rédaction et dépôt de la demande",
    minDuration: 0,
    maxDuration: 0,
    shortContent:
      "Rédigez la requête pour modifier ou annuler la pension, puis déposez-la au tribunal.",
    detailedContent: `
      Votre demande doit inclure :
      <ul class="list-disc list-inside">
        <li>Vos informations et celles de l'autre partie</li>
        <li>Les motifs justifiant la modification ou l'annulation</li>
        <li>Les nouvelles demandes concernant la pension</li>
      </ul>
      <p class="mt-2">
        Après la rédaction, déposez la requête avec les frais requis au greffe de la Cour supérieure.
      </p>
      <p><strong>Délai habituel :</strong> environ 1 mois pour préparer et déposer.</p>
    `,
    questions: [
      {
        id: "redaction_delay",
        label: "Durée prévue pour la rédaction (ex : '1')",
        type: "text",
        help: "Laissez vide pour 1 mois par défaut.",
      },
    ],
  },

  // Step = Signification (complex logic)
  {
    id: "step_signification",
    phase: "judiciaire",
    title: "Signification de la demande",
    minDuration: 0.25,
    maxDuration: 6,
    shortContent:
      "Faites signifier la requête à l'autre partie (adresse connue ou inconnue, etc.).",
    detailedContent: `
      Comme pour tout acte de procédure, vous devez faire signifier la demande.
      Les délais varient selon le lieu de résidence de l'autre partie, et selon
      que son adresse est connue ou non.
      <br /><br />
      <strong>En cas d'adresse inconnue :</strong> vous devrez demander un mode 
      spécial de signification au tribunal, ou effectuer un dépistage ou une
      publication dans un journal. 
    `,
    questions: [
      {
        id: "adresse_exacte",
        label: "Connaissez-vous l'adresse exacte de l'autre partie ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
        ],
      },
      {
        id: "contacts_recents",
        label: "Pouvez-vous obtenir l'adresse via la famille, proches, etc. ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
        ],
        showIf: (answers) =>
          answers["step_signification"]?.adresse_exacte === "non",
      },
      {
        id: "localisation",
        label: "Où réside l'autre partie ?",
        options: [
          {
            value: "quebec",
            label: "Au Québec (délai 1-2 semaines pour la signification)",
          },
          {
            value: "canada",
            label: "Ailleurs au Canada (1-3 semaines)",
          },
          {
            value: "etranger",
            label: "À l'étranger (délais variables)",
          },
        ],
        showIf: (answers) =>
          answers["step_signification"]?.adresse_exacte === "oui" ||
          answers["step_signification"]?.contacts_recents === "oui",
      },
      {
        id: "pays_etranger",
        label: "Dans quel pays réside-t-il/elle ?",
        type: "select",
        options: allCountries.map((c) => ({
          value: c.code,
          label: `${c.name}${
            c.isSignatory ? " (Convention La Haye: 0.5-2 mois)" : " (Hors Convention: 1-6 mois)"
          }`,
        })),
        showIf: (answers) =>
          answers["step_signification"]?.localisation === "etranger",
      },
      {
        id: "contacts_electroniques",
        label:
          "Avez-vous des contacts récents par e-mail ou réseaux sociaux (moins de 3 mois) ?",
        options: [
          {
            value: "oui",
            label: "Oui - Mode spécial (2-4 semaines avec autorisation)",
          },
          { value: "non", label: "Non" },
        ],
        showIf: (answers) =>
          answers["step_signification"]?.adresse_exacte === "non" &&
          answers["step_signification"]?.contacts_recents === "non",
      },
      {
        id: "residence_canada",
        label: "La personne a-t-elle déjà résidé au Canada ?",
        options: [
          {
            value: "oui",
            label: "Oui - Publication possible (2-3 semaines)",
          },
          { value: "non", label: "Non - Estimation incertaine" },
        ],
        showIf: (answers) =>
          answers["step_signification"]?.adresse_exacte === "non" &&
          answers["step_signification"]?.contacts_recents === "non" &&
          answers["step_signification"]?.contacts_electroniques === "non",
      },
      {
        id: "mode_special_decision",
        label: "Décision du juge sur le mode spécial de signification",
        options: [
          {
            value: "accepte",
            label: "Accepté (1-2 semaines après autorisation)",
          },
          {
            value: "refuse",
            label: "Refusé (retour à la case départ)",
          },
        ],
        showIf: (answers) =>
          answers["step_signification"]?.contacts_electroniques === "oui" ||
          answers["step_signification"]?.residence_canada === "oui",
      },
    ],
  },

  // Step = Réponse / Défense
  {
    id: "step_reponse",
    phase: "judiciaire",
    title: "Réponse / Défense de l'autre partie",
    minDuration: 0.5,
    maxDuration: 1,
    shortContent:
      "L'autre partie a 15 ou 30 jours pour répondre (ou moins en cas d'urgence).",
    detailedContent: `
      Une fois la signification effectuée, la partie adverse dispose 
      de 15 jours (au Québec) ou 30 jours (hors Québec) pour déposer 
      une réponse ou une défense. 
      <br /><br />
      En cas de mesures provisoires (urgence), le délai peut être réduit (3-10 jours).
    `,
    showIf: (answers) => {
      const s = answers["step_signification"] || {};
      // Hide if signification is impossible
      if (
        s.residence_canada === "non" &&
        s.contacts_electroniques === "non" &&
        s.adresse_exacte === "non" &&
        s.contacts_recents === "non"
      ) {
        return false;
      }
      // Also hide if mode special is refused => we might not proceed
      if (s.mode_special_decision === "refuse") {
        return false;
      }
      return true;
    },
    questions: [
      {
        id: "q_reponse_attendue",
        label: "Attendez-vous une réponse ou un accord ?",
        options: [
          { value: "non", label: "Aucune réponse attendue (jugement par défaut)" },
          { value: "oui_accord", label: "Oui, nous allons nous entendre" },
          { value: "oui_conflit", label: "Oui, il/elle va contester" },
        ],
      },
      {
        id: "q_delai_reponse",
        label: "Délai pour la réponse",
        options: (answers) => {
          // If urgent measures are activated somewhere:
          if (answers["pension_strategies"]?.mesures_provisoires === "oui") {
            return [
              {
                value: "3-10",
                label: "3-10 jours (mesures provisoires)",
                disabled: true,
              },
            ];
          }
          // Otherwise, depends on signification
          const s = answers["step_signification"] || {};
          if (s.adresse_exacte === "oui" || s.contacts_recents === "oui") {
            if (s.localisation === "quebec") {
              return [
                { value: "15", label: "15 jours (QC)", disabled: true },
              ];
            }
            if (s.localisation === "canada" || s.localisation === "etranger") {
              return [
                { value: "30", label: "30 jours (hors QC)", disabled: true },
              ];
            }
          }
          // fallback
          return [
            { value: "15-30", label: "15-30 jours (délai incertain)" },
          ];
        },
        showIf: (answers) =>
          answers["step_reponse"]?.q_reponse_attendue !== "non",
      },
    ],
  },

  // Step = Mesures provisoires (optionnel)
  {
    id: "pension_strategies",
    phase: "judiciaire",
    title: "Mesures provisoires / Mesures d'urgence",
    minDuration: 0,
    maxDuration: 1,
    shortContent: "Demandez-vous une audience rapide pour modifier la pension provisoirement ?",
    detailedContent: `
      Si la situation est urgente (par exemple, vous ne pouvez plus payer la pension
      et risquez un préjudice sérieux), vous pouvez demander des mesures provisoires. 
      Le tribunal statuera rapidement sur un montant transitoire, en attendant le 
      jugement final. 
    `,
    questions: [
      {
        id: "mesures_provisoires",
        label: "Avez-vous besoin de mesures provisoires ?",
        options: [
          { value: "non", label: "Non" },
          { value: "oui", label: "Oui (procédure accélérée)" },
        ],
      },
    ],
  },

  // Step = Consentement
  {
    id: "step_consentement",
    phase: "judiciaire",
    title: "Consentement (Accord mutuel)",
    minDuration: 0.25,
    maxDuration: 0.25,
    shortContent:
      "S'il y a accord, le juge l'homologuera sans audience conflictuelle.",
    detailedContent: `
      Si, après discussion, vous parvenez à un accord sur la modification de la pension, 
      vous pouvez soumettre cet accord au tribunal. 
      <br /><br />
      Le juge vérifiera que tout est conforme, puis l'homologuera 
      (transforme l'accord en jugement exécutoire).
    `,
    showIf: (answers) =>
      answers["step_reponse"]?.q_reponse_attendue === "oui_accord",
    questions: [],
  },

  // Step = Poursuite de la contestation
  {
    id: "step6",
    phase: "judiciaire",
    title: "Procédure contestée (absence d'accord)",
    minDuration: 1,
    maxDuration: 6,
    shortContent:
      "S'il n'y a pas d'accord, vous entamez un processus contradictoire (échanges de pièces, interrogatoires...).",
    detailedContent: `
      Sans consentement, la cause va suivre son cours devant le tribunal : 
      interrogatoires au préalable, communication de documents, etc. 
      Ce processus peut s'étendre sur plusieurs mois, selon la complexité.
    `,
    showIf: (answers) => answers["step_reponse"]?.q_reponse_attendue === "oui_conflit",
    questions: [],
  },

  // Step = Remises
  {
    id: "step_remises",
    phase: "judiciaire",
    title: "Remises et délais supplémentaires",
    minDuration: 0,
    maxDuration: 3,
    shortContent: "Les avocats peuvent demander des reports, allongeant la procédure.",
    detailedContent: `
      Des demandes de remise d'audience (reports) peuvent se produire pour diverses raisons 
      (besoin d'expertises supplémentaires, etc.), ce qui ajoute parfois 
      plusieurs semaines ou mois au calendrier initial.
    `,
    showIf: (answers) => answers["step_reponse"]?.q_reponse_attendue === "oui_conflit",
    questions: [
      {
        id: "q_remise",
        label: "Prévoyez-vous des reports probables ?",
        options: [
          { value: "non", label: "Non" },
          { value: "oui", label: "Oui" },
        ],
      },
    ],
  },

  // Step = Délai avant l'audience (ou procès)
  {
    id: "step_delai_proces",
    phase: "judiciaire",
    title: "Délai avant l'audience finale",
    minDuration: 12,
    maxDuration: 18,
    shortContent:
      "Le tribunal fixe l'audience finale, typiquement 12 à 18 mois plus tard (selon engorgement).",
    detailedContent: `
      S'il y a vraiment contestation, il peut se passer de longs mois avant l'audience 
      finale, surtout dans les grands centres urbains où les rôles sont chargés.
    `,
    showIf: (answers) => answers["step_reponse"]?.q_reponse_attendue === "oui_conflit",
    questions: [],
  },

  // Step = Règlement avant l'audience
  {
    id: "step_reglement_avant",
    phase: "judiciaire",
    title: "Règlement à l'amiable avant l'audience",
    minDuration: 0,
    maxDuration: 0,
    shortContent: "Vous pouvez encore conclure une entente avant d'aller en salle d'audience.",
    detailedContent: `
      Même si la procédure est enclenchée, vous pouvez vous entendre 
      jusqu'à la veille de l'audience. Dans ce cas, 
      vous soumettez l'accord au juge pour homologation.
      <div class="mt-2 p-2 bg-yellow-50 rounded">
        <p class="text-sm font-semibold">Astuce :</p>
        <p class="text-sm">Si vous parvenez à un accord, vous pourriez revenir en arrière 
        et sélectionner "Oui, accord" à la question sur la réponse du conjoint, 
        pour recalculer un délai réduit.</p>
      </div>
    `,
    showIf: (answers) => answers["step_reponse"]?.q_reponse_attendue === "oui_conflit",
    questions: [
      {
        id: "q_reglement_avant",
        label: "Souhaitez-vous conclure un accord avant l'audience ?",
        options: [
          {
            value: "oui",
            label:
              "Oui - On refait l'estimation avec la procédure par consentement",
          },
          {
            value: "non",
            label: "Non - On continue jusqu'à l'audience",
          },
        ],
      },
    ],
  },

  // Step = Audience / procès
  {
    id: "step7",
    phase: "judiciaire",
    title: "Audience / Procès",
    minDuration: 2,
    maxDuration: 6,
    shortContent:
      "Le juge entend les parties (quelques heures à quelques jours selon la complexité).",
    detailedContent: `
      Chaque partie présente ses arguments et ses preuves.
      Après l'audience, le juge peut rendre sa décision immédiatement 
      ou prendre quelques semaines (délibéré) avant de prononcer son jugement.
    `,
    showIf: (answers) =>
      answers["step_reponse"]?.q_reponse_attendue === "oui_conflit" &&
      answers["step_reglement_avant"]?.q_reglement_avant === "non",
    questions: [],
  },

  // Step = Jugement par défaut
  {
    id: "step_default_judgement",
    phase: "judiciaire",
    title: "Jugement par défaut",
    minDuration: 1,
    maxDuration: 2,
    shortContent:
      "Si l'autre partie ne répond pas du tout, le tribunal peut rendre un jugement par défaut.",
    detailedContent: `
      Après l'expiration du délai de réponse, 
      Après l'expiration du délai de réponse, 
      vous pouvez demander au tribunal de rendre un jugement par défaut. 
      Le juge vérifie vos documents et preuves (ex : changement de revenus, etc.) 
      et rend sa décision sans la partie adverse.
    `,
    showIf: (answers) => answers["step_reponse"]?.q_reponse_attendue === "non",
    questions: [],
  },

  // Step = Jugement final
  {
    id: "step8",
    phase: "judiciaire",
    title: "Jugement final & mise à jour",
    minDuration: 1,
    maxDuration: 7,
    shortContent:
      "Le juge rend son ordonnance modifiant ou annulant la pension ; quelques semaines à plusieurs mois selon la complexité.",
    detailedContent: `
      Une fois que le juge a rendu son jugement, 
      vous pouvez faire mettre à jour les registres (ex : Revenu Québec si c'est eux qui perçoivent la pension).
      Le juge peut prendre jusqu'à quelques mois pour officialiser le jugement écrit, 
      puis vous pourrez le faire exécuter.
    `,
    questions: [],
  },

  // Step = Mesures provisoires (détails)
  {
    id: "step_provisoires",
    phase: "judiciaire",
    title: "Procédure de mesures provisoires",
    minDuration: 0.25,
    maxDuration: 0.5,
    shortContent:
      "En cas d'urgence, audience accélérée pour obtenir un ajustement provisoire de la pension.",
    detailedContent: `
      <p>Si vous avez indiqué avoir besoin de mesures provisoires, sachez que :</p>
      <ul class="list-disc list-inside">
        <li>Le délai de réponse est réduit (3-10 jours)</li>
        <li>Le tribunal peut tenir une audience rapide pour décider d'un montant transitoire</li>
        <li>Cette décision demeure temporaire jusqu'au jugement final</li>
      </ul>
    `,
    showIf: (answers) => answers["pension_strategies"]?.mesures_provisoires === "oui",
    questions: [
      {
        id: "type_urgence",
        label: "Type de mesures provisoires demandées",
        options: [
          { value: "baisse", label: "Baisse de la pension" },
          { value: "suspension", label: "Suspension totale" },
          { value: "urgence_medicale", label: "Autre situation urgente" },
        ],
      },
    ],
  },
];

// PHASE 3: (CONTENTIEUX) 
// If you want a separate set of steps for a fully contested scenario, you could define them here. 
// However, we've integrated everything into the "judiciaire" phase above, 
// so you might skip "contentieux" or keep it only for additional details.

const contentieuxSteps = [
  {
    id: "step_contentieux_intro",
    phase: "contentieux",
    title: "Procédure Contentieuse (si aucune entente n'est possible)",
    minDuration: 0,
    maxDuration: 0,
    shortContent:
      "Le dossier suit les voies judiciaires classiques : signification, défense, audience, etc.",
    detailedContent: `
      Cette section s'applique si vous n'êtes pas éligible à une procédure simple (entente) 
      ou si la partie adverse s'y oppose fermement.
    `,
    questions: [],
    showIf: (answers) => answers["pension_prep"]?.q_entente === "non",
  },
];

// PHASE 4: POST-PROCÉDURE
const postSteps = [
  {
    id: "pension_post",
    phase: "post",
    title: "Formalités post-jugement",
    minDuration: 0,
    maxDuration: 0,
    shortContent:
      "Après le jugement, informez Revenu Québec ou tout autre organisme. Conservez une copie.",
    detailedContent: `
      Une fois la nouvelle décision en main :
      <ul class="list-disc list-inside">
        <li>Transmettez-en une copie à Revenu Québec si c'est eux qui appliquent la retenue sur salaire</li>
        <li>Conservez toutes les pièces justificatives</li>
        <li>Si la pension est annulée, vérifiez l'arrêt des prélèvements</li>
      </ul>
    `,
    questions: [],
  },
];

// Combine everything
function getAllSteps() {
  return [
    ...preparationSteps,
    ...judiciaireSteps,
    ...contentieuxSteps,
    ...postSteps,
  ];
}

// PHASES ORDER
const PHASES_ORDER = [
  { id: "preparation", label: "Phase 1 : Préparation" },
  { id: "judiciaire", label: "Phase 2 : Processus judiciaire" },
  { id: "contentieux", label: "Phase 3 : Procédure contentieuse (optionnel)" },
  { id: "post", label: "Phase 4 : Après le jugement" },
];

// ─────────────────────────────────────────────────────────────────────────────
// 3) CALCULATE TIMELINE
// ─────────────────────────────────────────────────────────────────────────────

function calculateTimeline(answers, completedSteps) {
  let totalMin = 0;
  let totalMax = 0;

  // We store text logs
  const phaseHistory = {
    preparation: [],
    judiciaire: [],
    contentieux: [],
    post: [],
  };

  // Filter steps by showIf
  const visibleSteps = getAllSteps().filter((step) => {
    if (step.showIf && step.showIf(answers) === false) return false;
    return true;
  });

  // Helper to compute each step's duration
  function computeStepDuration(step) {
    if (!completedSteps.includes(step.id)) {
      return { min: 0, max: 0 };
    }

    // Example: custom doc delay for "pension_prep"
    if (step.id === "pension_prep") {
      const userVal = parseMonths(answers["pension_prep"]?.docs_delay);
      if (userVal > 0) {
        phaseHistory.preparation.push(
          `[${step.title}] Délai perso: ${userVal} mois`
        );
        return { min: userVal, max: userVal };
      }
      phaseHistory.preparation.push(
        `[${step.title}] Délai par défaut: ${DEFAULT_DOCS_MIN}-${DEFAULT_DOCS_MAX} mois`
      );
      return { min: DEFAULT_DOCS_MIN, max: DEFAULT_DOCS_MAX };
    }

    // Example: step3 => redaction_delay
    if (step.id === "pension_step3") {
      const rd = parseMonths(answers["pension_step3"]?.redaction_delay);
      if (rd > 0) {
        phaseHistory.judiciaire.push(
          `[${step.title}] Délai perso: ${rd} mois`
        );
        return { min: rd, max: rd };
      }
      phaseHistory.judiciaire.push(
        `[${step.title}] Délai par défaut: ${DEFAULT_REDACTION} mois`
      );
      return { min: DEFAULT_REDACTION, max: DEFAULT_REDACTION };
    }

    // If it's the signification step with advanced logic
    if (step.id === "step_signification") {
      const delay = calculateSignificationDelay(answers);
      phaseHistory.judiciaire.push(delay.msg);
      return { min: delay.min, max: delay.max };
    }

    // If it's the "response" step => custom logic
    if (step.id === "step_reponse") {
      const rep = answers["step_reponse"] || {};
      let minDelay = 0.5,
        maxDelay = 1;
      if (rep.q_reponse_attendue === "non") {
        phaseHistory.judiciaire.push(
          "[Réponse] Aucune réponse attendue => jugement par défaut possible"
        );
      } else if (rep.q_reponse_attendue === "oui_accord") {
        minDelay = 0.25;
        maxDelay = 0.5;
        phaseHistory.judiciaire.push("[Réponse] Accord mutuel: 1-2 semaines");
      } else if (rep.q_delai_reponse === "30") {
        minDelay = 1;
        maxDelay = 1.5;
        phaseHistory.judiciaire.push("[Réponse] Hors QC => 1-1.5 mois");
      } else if (rep.q_delai_reponse === "15") {
        minDelay = 0.5;
        maxDelay = 0.75;
        phaseHistory.judiciaire.push("[Réponse] Québec => ~0.5-0.75 mois");
      } else if (rep.q_delai_reponse === "3-10") {
        minDelay = 0.1;
        maxDelay = 0.3;
        phaseHistory.judiciaire.push("[Réponse] Urgence (3-10 jours)");
      }
      return { min: minDelay, max: maxDelay };
    }

    // If it's step_provisoires => small bump
    if (step.id === "step_provisoires") {
      phaseHistory.judiciaire.push(
        "[Mesures provisoires] +1-2 semaines"
      );
      return { min: step.minDuration, max: step.maxDuration };
    }

    // If it's step_remises => maybe +1 month if user says "oui"
    if (step.id === "step_remises") {
      if (answers["step_remises"]?.q_remise === "oui") {
        phaseHistory.judiciaire.push("[Remises] +1 mois");
        return { min: 1, max: 1 };
      }
      return { min: 0, max: 0 };
    }

    // By default
    phaseHistory[step.phase].push(
      `[${step.title}] Durée: ${step.minDuration}-${step.maxDuration} mois`
    );
    return { min: step.minDuration, max: step.maxDuration };
  }

  // Loop over visible steps
  visibleSteps.forEach((step) => {
    const { min, max } = computeStepDuration(step);
    totalMin += min;
    totalMax += max;
  });

  return { totalMin, totalMax, phaseHistory };
}

// ─────────────────────────────────────────────────────────────────────────────
// 4) MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function PensionTimeline() {
  const { t, i18n } = useTranslation(['pension', 'common']);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [openStep, setOpenStep] = useState(null);
  const [answers, setAnswers] = useState({});
  const [timelineSummary, setTimelineSummary] = useState({
    totalMin: 0,
    totalMax: 0,
    phaseHistory: { preparation: [], judiciaire: [], contentieux: [], post: [] },
  });

  // Force component update when language changes
  useEffect(() => {
    // Adding a console log to verify language changes are detected
    console.log("Language changed to:", i18n.language);
    
    // Force re-render by updating a state value
    setTimelineSummary(prev => ({...prev}));
  }, [i18n.language]);

  // Recompute timeline any time answers or completedSteps change
  useEffect(() => {
    const { totalMin, totalMax, phaseHistory } = calculateTimeline(
      answers,
      completedSteps
    );
    setTimelineSummary({ totalMin, totalMax, phaseHistory });

    // Example: auto adjusting reponse delay if "mesures_provisoires"
    const stepSignif = answers["step_signification"] || {};
    if (answers["pension_strategies"]?.mesures_provisoires === "oui") {
      setAnswers((prev) => ({
        ...prev,
        step_reponse: {
          ...prev.step_reponse,
          q_delai_reponse: "3-10",
        },
      }));
    } else if (stepSignif.adresse_exacte === "oui" || stepSignif.contacts_recents === "oui") {
      if (stepSignif.localisation === "quebec") {
        setAnswers((prev) => ({
          ...prev,
          step_reponse: {
            ...prev.step_reponse,
            q_delai_reponse: "15",
          },
        }));
      } else if (stepSignif.localisation === "canada" || stepSignif.localisation === "etranger") {
        setAnswers((prev) => ({
          ...prev,
          step_reponse: {
            ...prev.step_reponse,
            q_delai_reponse: "30",
          },
        }));
      }
    }
  }, [answers, completedSteps]);

  // Handle answer changes
  const handleAnswerChange = (stepId, questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [stepId]: {
        ...prev[stepId],
        [questionId]: value,
      },
    }));
  };

  // Check if all questions in a step are answered
  const areAllQuestionsAnswered = (step) => {
    if (!step.questions?.length) return true;
    const stepAns = answers[step.id] || {};
    return step.questions.every((q) => {
      if (q.showIf && !q.showIf(answers)) return true; // not displayed => skip
      // Some fields can be optional (like doc delays), so skip the emptiness check if that's intended
      if (["docs_delay", "redaction_delay"].includes(q.id)) return true;
      // Otherwise, we want a non-empty response
      return !!stepAns[q.id];
    });
  };

  // Mark step as done
  const markStepAsDone = (step) => {
    if (!areAllQuestionsAnswered(step)) {
      alert(t('pension:pensionGuide.alerts.completeRequired'));
      return;
    }
    // Ensure previous steps are done
    const visibleSteps = getAllSteps().filter(
      (s) => !s.showIf || s.showIf(answers) !== false
    );
    const currentIndex = visibleSteps.findIndex((s) => s.id === step.id);
    for (let i = 0; i < currentIndex; i++) {
      if (!completedSteps.includes(visibleSteps[i].id)) {
        alert(t('pension:pensionGuide.alerts.completePrevious'));
        return;
      }
    }

    if (window.confirm(t('pension:pensionGuide.alerts.confirmStep', { step: step.title }))) {
      if (!completedSteps.includes(step.id)) {
        setCompletedSteps((prev) => [...prev, step.id]);
      }
      setOpenStep(null);

      // If user chooses "oui" in step_reglement_avant => reinit
      if (
        step.id === "step_reglement_avant" &&
        answers[step.id]?.q_reglement_avant === "oui"
      ) {
        if (
          window.confirm(
            "Vous souhaitez conclure un accord. Voulez-vous réinitialiser pour recalculer un parcours simplifié ?"
          )
        ) {
          resetAll();
        }
      }
    }
  };

  // Reset everything
  const resetAll = () => {
    if (window.confirm(t('pension:pensionGuide.actions.resetConfirm'))) {
      setCompletedSteps([]);
      setOpenStep(null);
      setAnswers({});
      setTimelineSummary({
        totalMin: 0,
        totalMax: 0,
        phaseHistory: { preparation: [], judiciaire: [], contentieux: [], post: [] },
      });
    }
  };

  // For a progress bar measure
  const visibleSteps = getAllSteps().filter(
    (s) => !s.showIf || s.showIf(answers) !== false
  );

  // A simplified approach to compute a "planned total"
  const getPlannedStepDelay = (step) => {
    // For the "pension_prep" step
    if (step.id === "pension_prep") {
      const customVal = parseMonths(answers["pension_prep"]?.docs_delay);
      return customVal || step.minDuration;
    }
    // For the "pension_step3" step
    if (step.id === "pension_step3") {
      const customVal = parseMonths(answers["pension_step3"]?.redaction_delay);
      return customVal || DEFAULT_REDACTION;
    }
    // For the signification step, we can just use step.minDuration as an estimate
    // or replicate the logic from calculateSignificationDelay in short form
    return step.minDuration;
  };

  let plannedTotal = visibleSteps.reduce((acc, st) => acc + getPlannedStepDelay(st), 0);

  const { totalMin, totalMax, phaseHistory } = timelineSummary;
  const progressPercent =
    plannedTotal > 0 ? Math.min(Math.round((totalMin / plannedTotal) * 100), 100) : 0;

  // Group steps by phase for rendering
  const groupedPhases = PHASES_ORDER.map((ph) => {
    const stepsInThisPhase = visibleSteps.filter((s) => s.phase === ph.id);
    return { ...ph, steps: stepsInThisPhase };
  });

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="px-6 py-8 mt-4 bg-blue-50 rounded-xl border border-blue-100 shadow-sm mb-8">
        <h1 className="text-2xl font-bold text-blue-700 mb-3 text-center">
          <span className="inline-block h-1 bg-warning w-8 mr-2 rounded-full align-middle"></span>
          {t('pension:pensionGuide.timeline.title')}
          <span className="inline-block h-1 bg-warning w-8 ml-2 rounded-full align-middle"></span>
        </h1>
        <p className="text-center text-blue-600 max-w-3xl mx-auto">
          {t('pension:pensionGuide.timeline.subtitle')}
        </p>
      </div>

      {/* Top 3 cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-8 px-4">
        {/* Card 1: Progression */}
        <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
          <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
            <span className="bg-blue-50 p-2 rounded-full mr-2">📊</span>
            {t('pension:pensionGuide.interface.cards.progress.title')}
          </h3>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="font-semibold">{progressPercent}% {t('pension:pensionGuide.interface.cards.progress.completed')}</span>
              <span className="text-slate-500 text-xs">
                {totalMin.toFixed(1)} / {plannedTotal.toFixed(1)} {t('pension:pensionGuide.interface.cards.progress.months')}
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-3">
              <div
                className="bg-blue-500 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Card 2: Estimation */}
        <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
          <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
            <span className="bg-blue-50 p-2 rounded-full mr-2">⏱️</span>
            {t('pension:pensionGuide.interface.cards.estimation.title')}
          </h3>
          <div className="flex items-center justify-center h-16">
            {totalMin === 0 && totalMax === 0 ? (
              <p className="text-slate-500 text-center text-sm">
                {t('pension:pensionGuide.interface.cards.estimation.completeStepsPrompt')}
              </p>
            ) : (
              <div className="text-center">
                <span className="text-2xl font-bold text-blue-700">
                  {totalMin} {t('pension:pensionGuide.interface.cards.estimation.to')} {totalMax} {t('pension:pensionGuide.interface.cards.estimation.months')}
                </span>
                <p className="text-xs text-slate-500 mt-1">{t('pension:pensionGuide.interface.cards.estimation.basedOnAnswers')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Card 3: Actions */}
        <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
          <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
            <span className="bg-blue-50 p-2 rounded-full mr-2">🔄</span>
            {t('pension:pensionGuide.interface.cards.actions.title')}
          </h3>
          <div className="flex justify-center items-center h-16">
            <button
              onClick={resetAll}
              className="btn btn-outline border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400"
            >
              {t('pension:pensionGuide.interface.cards.actions.reset')}
            </button>
          </div>
        </div>
      </div>

      {/* Phase History */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-8 mx-4">
        <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
          <span className="bg-blue-50 p-2 rounded-full mr-2">📋</span>
          {t('pension:pensionGuide.interface.details.title')}
        </h3>
        {Object.entries(phaseHistory).some(([_, list]) => list.length > 0) ? (
          <div className="space-y-4">
            {Object.entries(phaseHistory).map(([phase, msgs]) => {
              if (!msgs.length) return null;
              return (
                <div key={phase} className={`p-3 rounded-lg ${
                  phase === "preparation"
                    ? "bg-blue-100 border-blue-200 text-blue-700"
                    : phase === "judiciaire"
                    ? "bg-amber-100 border-amber-200 text-amber-700"
                    : "bg-green-100 border-green-200 text-green-700"
                }`}>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <span className="mr-2">
                      {phase === "preparation" ? "📋" : phase === "judiciaire" ? "⚖️" : "📝"}
                    </span>
                    {t(`pension:pensionGuide.phases.${phase === "preparation" ? "preparation" : phase === "judiciaire" ? "judicial" : "postDecision"}.title`)}
                  </h4>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    {msgs.map((m, idx) => (
                      <li key={idx} className="text-slate-700">
                        {m}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center p-4 text-slate-500">
            <p>{t('pension:pensionGuide.interface.details.noData')}</p>
          </div>
        )}
      </div>

      {/* Steps by phase */}
      <div className="space-y-6 px-4 mb-8">
        {groupedPhases.map((phaseObj) => {
          if (!phaseObj.steps.length) return null;

          return (
            <div key={phaseObj.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {/* Phase header */}
              <div className={`p-4 ${
                phaseObj.id === "preparation"
                  ? "bg-blue-100 text-blue-700"
                  : phaseObj.id === "judiciaire"
                  ? "bg-amber-100 text-amber-700"
                  : "bg-green-100 text-green-700"
              }`}>
                <h2 className="text-lg font-bold flex items-center">
                  <span className="mr-3 text-2xl">
                    {phaseObj.id === "preparation" ? "📋" : phaseObj.id === "judiciaire" ? "⚖️" : "📝"}
                  </span>
                  {phaseObj.label}
                </h2>
              </div>

              {/* Steps inside this phase */}
              <div className="p-4 relative">
                <div className="absolute w-[4px] h-full left-6 top-0 rounded-full z-0 bg-gradient-to-b from-blue-100 via-amber-100 to-green-100"></div>

                <div className="space-y-8">
                  {phaseObj.steps.map((step, idx) => {
                    const globalIndex = getAllSteps().findIndex((x) => x.id === step.id);
                    const isDone = completedSteps.includes(step.id);
                    const isOpen = openStep === step.id;
                    const isActive = isOpen && !isDone;

                    let style = {
                      bg: "bg-white",
                      border: "border-slate-200",
                      text: "text-slate-700",
                      bulletBg: "bg-white",
                      bulletBorder: "border-slate-300",
                      bulletText: "text-slate-700",
                    };

                    if (isDone) {
                      style = {
                        bg: "bg-green-50",
                        border: "border-green-200",
                        text: "text-green-700",
                        bulletBg: "bg-green-500",
                        bulletBorder: "border-green-500",
                        bulletText: "text-white",
                      };
                    } else if (isActive) {
                      style = {
                        bg: "bg-amber-50",
                        border: "border-amber-200",
                        text: "text-amber-700",
                        bulletBg: "bg-amber-50",
                        bulletBorder: "border-amber-500",
                        bulletText: "text-amber-700",
                      };
                    }

                    return (
                      <div key={step.id} className="flex flex-col md:flex-row relative z-10">
                        <div className="mr-6">
                          <div
                            className={`w-12 h-12 rounded-full border-4 shadow-sm flex items-center justify-center text-lg font-bold 
                              ${style.bulletBg} ${style.bulletBorder} ${style.bulletText}
                              transition-all duration-300 ease-in-out
                              ${isDone ? "scale-110" : ""}
                            `}
                            style={{ marginLeft: "-4px" }}
                          >
                            {isDone ? "✓" : globalIndex + 1}
                          </div>
                        </div>

                        <div className="flex-1 pt-1">
                          <div
                            className={`card w-full border ${style.border} ${style.bg} rounded-xl shadow-sm
                            hover:shadow-md transition-all duration-200 ease-in-out
                            ${isActive ? "transform scale-[1.02]" : ""}
                            `}
                          >
                            <div className="card-body p-5">
                              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                                <h3 className={`card-title text-lg ${style.text}`}>
                                  {step.title}
                                </h3>
                              </div>
                              <p className="mt-3 text-slate-600">
                                {step.shortContent}
                              </p>

                              {/* Buttons */}
                              <div className="mt-4 flex gap-3 flex-wrap">
                                <button
                                  className={`btn btn-sm rounded-full px-4 ${
                                    isOpen
                                      ? "bg-amber-100 border-amber-200 text-amber-700 hover:bg-amber-200"
                                      : "bg-blue-100 border-blue-200 text-blue-700 hover:bg-blue-200"
                                  }`}
                                  onClick={() => setOpenStep(isOpen ? null : step.id)}
                                >
                                  {isOpen ? (
                                    <>
                                      Masquer
                                      <span className="ml-1">▲</span>
                                    </>
                                  ) : (
                                    <>
                                      Détails & Questions
                                      <span className="ml-1">▼</span>
                                    </>
                                  )}
                                </button>

                                {!isDone && (
                                  <button
                                    className="btn btn-sm rounded-full px-4 bg-green-100 border-green-200 text-green-700 hover:bg-green-200"
                                    onClick={() => markStepAsDone(step)}
                                  >
                                    ✓ Terminer
                                  </button>
                                )}

                                {isDone && (
                                  <span className="text-green-600 bg-green-50 px-3 py-1 rounded-full text-sm font-semibold border border-green-100 self-center">
                                    Étape terminée
                                  </span>
                                )}
                              </div>

                              {/* Accordion content */}
                              {isOpen && (
                                <div className="mt-5 animate-[fadeIn_0.3s_ease-in-out]">
                                  <style>{`
                                    @keyframes fadeIn {
                                      from { opacity: 0; transform: translateY(-10px); }
                                      to { opacity: 1; transform: translateY(0); }
                                    }
                                  `}</style>

                                  {/* Detailed content */}
                                  <div className="p-4 border border-slate-200 rounded-lg bg-white text-slate-600 mb-4">
                                    <h4 className="font-semibold text-blue-700 mb-2">
                                      Détails de l'étape
                                    </h4>
                                    {typeof step.detailedContent === "string" ? (
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: step.detailedContent,
                                        }}
                                      />
                                    ) : (
                                      step.detailedContent
                                    )}
                                  </div>

                                  {/* Questions */}
                                  {step.questions?.length > 0 && (
                                    <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                                      <h4 className="font-semibold text-amber-700 mb-3 flex items-center">
                                        <span className="mr-2">❓</span>
                                        Questions pour adapter l'estimation
                                      </h4>
                                      <div className="space-y-5">
                                        {step.questions.map((q) => {
                                          if (typeof q.showIf === "function" && !q.showIf(answers)) {
                                            return null;
                                          }
                                          const stepAns = answers[step.id] || {};
                                          const selectedValue = stepAns[q.id] || "";

                                          if (q.type === "text" || q.type === "number") {
                                            return (
                                              <div
                                                key={q.id}
                                                className="bg-white p-3 rounded-lg border border-amber-100"
                                              >
                                                <label className="font-semibold block mb-1 text-blue-700">
                                                  {q.label}
                                                </label>
                                                {q.help && (
                                                  <p className="text-xs text-slate-500 mb-3">
                                                    {q.help}
                                                  </p>
                                                )}
                                                <input
                                                  type={q.type}
                                                  className="input input-bordered w-full max-w-md border-slate-300 focus:border-blue-400 focus:ring-blue-200"
                                                  value={selectedValue}
                                                  onChange={(e) =>
                                                    handleAnswerChange(
                                                      step.id,
                                                      q.id,
                                                      e.target.value
                                                    )
                                                  }
                                                  placeholder={q.type === "text" ? "Votre réponse..." : "0"}
                                                />
                                              </div>
                                            );
                                          }

                                          if (q.type === "select") {
                                            return (
                                              <div
                                                key={q.id}
                                                className="bg-white p-3 rounded-lg border border-amber-100"
                                              >
                                                <label className="font-semibold block mb-1 text-blue-700">
                                                  {q.label}
                                                </label>
                                                {q.help && (
                                                  <p className="text-xs text-slate-500 mb-3">
                                                    {q.help}
                                                  </p>
                                                )}
                                                <select
                                                  className="select select-bordered w-full max-w-md border-slate-300 focus:border-blue-400 focus:ring-blue-200"
                                                  value={selectedValue}
                                                  onChange={(e) =>
                                                    handleAnswerChange(
                                                      step.id,
                                                      q.id,
                                                      e.target.value
                                                    )
                                                  }
                                                >
                                                  <option value="">
                                                    -- Sélectionnez une option --
                                                  </option>
                                                  {q.options?.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                      {opt.label}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                            );
                                          }

                                          if (q.options && !q.type) {
                                            const ops =
                                              typeof q.options === "function"
                                                ? q.options(answers)
                                                : q.options;
                                            return (
                                              <div
                                                key={q.id}
                                                className="bg-white p-3 rounded-lg border border-amber-100"
                                              >
                                                <label className="font-semibold block mb-1 text-blue-700">
                                                  {q.label}
                                                </label>
                                                {q.help && (
                                                  <p className="text-xs text-slate-500 mb-3">
                                                    {q.help}
                                                  </p>
                                                )}
                                                <div className="space-y-2 mt-3">
                                                  {ops.map((opt) => (
                                                    <label
                                                      key={opt.value}
                                                      className={`flex items-center gap-2 p-2 hover:bg-blue-50 rounded-md transition-colors
                                                        ${
                                                          selectedValue === opt.value
                                                            ? "bg-blue-50 border border-blue-100"
                                                            : ""
                                                        }
                                                        ${
                                                          opt.disabled
                                                            ? "opacity-50 cursor-not-allowed"
                                                            : "cursor-pointer"
                                                        }
                                                      `}
                                                    >
                                                      <input
                                                        type="radio"
                                                        className="radio radio-sm radio-primary"
                                                        name={`${step.id}-${q.id}`}
                                                        value={opt.value}
                                                        checked={selectedValue === opt.value}
                                                        onChange={() => {
                                                          if (!opt.disabled) {
                                                            handleAnswerChange(step.id, q.id, opt.value);
                                                          }
                                                        }}
                                                        disabled={opt.disabled}
                                                      />
                                                      <span>{opt.label}</span>
                                                    </label>
                                                  ))}
                                                </div>
                                              </div>
                                            );
                                          }

                                          return null;
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legal note */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600 mb-8 mx-4">
        <p className="italic text-center">
          {t('pension:pensionGuide.legalNote')}
        </p>
      </div>
    </div>
  );
}
